<template>
  <div class="shuffle-page">
    <div class="shuffle-header">
      <h1>Shuffle</h1>
      <p class="stats" v-if="releases.length">
        {{ pool.length }} of {{ releases.length }} records match
      </p>
    </div>

    <div v-if="isLoading" class="status">Loading your collection…</div>
    <div v-else-if="error" class="status error">{{ error }}</div>

    <template v-else>
      <section v-for="group in options" :key="group.key" class="filter-group">
        <h2>{{ group.label }}</h2>
        <div class="chips">
          <button
            v-for="chip in group.chips"
            :key="chip.value"
            type="button"
            class="chip"
            :class="{ active: chip.active }"
            :aria-pressed="chip.active"
            @click="toggle(group.key, chip.value)"
          >
            {{ chip.value }} <span class="chip-count">{{ chip.count }}</span>
          </button>
        </div>
      </section>

      <div class="deal-bar">
        <div class="deal-count" role="group" aria-label="How many records">
          <button
            v-for="n in DEAL_SIZES"
            :key="n"
            type="button"
            class="count-btn"
            :class="{ active: dealSize === n }"
            :aria-pressed="dealSize === n"
            @click="dealSize = n"
          >{{ n }}</button>
        </div>
        <button type="button" class="deal-btn" :disabled="!pool.length" @click="deal(dealSize)">
          Deal {{ dealSize }}
        </button>
        <button type="button" class="clear-btn" @click="clearFilters">Clear filters</button>
      </div>

      <div v-if="dealt.length" class="dealt-grid">
        <article v-for="record in dealt" :key="record.instanceId" class="dealt-card">
          <a :href="record.url" target="_blank" rel="noreferrer" class="cover-link">
            <img :src="record.coverImage" :alt="record.title" class="cover" loading="lazy">
          </a>
          <div class="card-body">
            <h3>{{ record.title }}</h3>
            <p class="artist">{{ record.artists.join(', ') }}</p>
            <p class="year" v-if="record.originalYear">{{ record.originalYear }}</p>
            <div class="tags">
              <span v-for="tag in record.moods" :key="tag" class="tag mood">{{ tag }}</span>
              <span v-for="tag in record.genres" :key="tag" class="tag genre">{{ tag }}</span>
            </div>
            <p v-if="record.note" class="note">{{ record.note }}</p>
          </div>
        </article>
      </div>
      <p v-else-if="pool.length" class="status">Pick some filters, then deal.</p>
      <p v-else class="status">Nothing matches — loosen a filter.</p>
    </template>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import useShuffle from '@/composables/useShuffle';

const DEAL_SIZES = [1, 3, 5, 10];
const dealSize = ref(3);

const {
  releases, isLoading, error, options, pool, dealt, toggle, clearFilters, deal, load,
} = useShuffle();

onMounted(load);
</script>

<style scoped>
.shuffle-page {
  max-width: var(--page-max);
  margin: 0 auto;
  padding: var(--space-lg) var(--page-gutter) var(--space-xl);
  color: var(--color-text);
}

.shuffle-header {
  display: flex;
  align-items: baseline;
  gap: var(--space-md);
  margin-bottom: var(--space-lg);
}

.shuffle-header h1 {
  font-size: var(--font-size-xl);
  margin: 0;
}

.stats,
.status {
  color: var(--color-text-secondary);
  font-size: var(--font-size-sm);
}

.status.error {
  color: #ff6b6b;
}

.filter-group {
  margin-bottom: var(--space-lg);
}

.filter-group h2 {
  font-size: var(--font-size-xs);
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: var(--color-text-secondary);
  margin: 0 0 var(--space-sm);
}

.chips {
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-sm);
}

.chip,
.count-btn,
.clear-btn {
  background: var(--color-surface-light);
  color: var(--color-text);
  border: 1px solid transparent;
  border-radius: 999px;
  padding: 6px 14px;
  font-size: var(--font-size-sm);
  cursor: pointer;
  transition: background 0.15s ease, border-color 0.15s ease;
}

.chip:hover,
.count-btn:hover,
.clear-btn:hover {
  background: var(--color-surface-lighter);
}

.chip.active,
.count-btn.active {
  background: var(--color-primary);
  color: #000;
}

.chip-count {
  opacity: 0.6;
  margin-left: 4px;
  font-size: var(--font-size-xs);
}

.deal-bar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--space-md);
  padding: var(--space-md) 0 var(--space-xl);
  border-bottom: 1px solid var(--color-divider);
  margin-bottom: var(--space-xl);
}

.deal-count {
  display: flex;
  gap: var(--space-xs);
}

.deal-btn {
  background: var(--color-primary);
  color: #000;
  font-weight: 700;
  border: none;
  border-radius: 999px;
  padding: 10px 28px;
  font-size: var(--font-size-md);
  cursor: pointer;
}

.deal-btn:hover:not(:disabled) {
  background: var(--color-primary-light);
}

.deal-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.dealt-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
  gap: var(--space-lg);
}

.dealt-card {
  background: var(--color-surface);
  border-radius: var(--border-radius);
  overflow: hidden;
  box-shadow: var(--shadow-card);
}

.cover {
  display: block;
  width: 100%;
  aspect-ratio: 1;
  object-fit: cover;
}

.card-body {
  padding: var(--space-md);
}

.card-body h3 {
  margin: 0 0 var(--space-xs);
  font-size: var(--font-size-md);
}

.artist,
.year {
  margin: 0;
  color: var(--color-text-secondary);
  font-size: var(--font-size-sm);
}

.tags {
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-xs);
  margin: var(--space-sm) 0;
}

.tag {
  font-size: var(--font-size-xs);
  padding: 2px 8px;
  border-radius: 999px;
}

.tag.mood {
  background: rgba(29, 185, 84, 0.2);
  color: var(--color-primary-light);
}

.tag.genre {
  background: var(--color-surface-lighter);
  color: var(--color-text-secondary);
}

.note {
  margin: 0;
  font-size: var(--font-size-sm);
  line-height: 1.45;
  color: var(--color-text);
}
</style>
